/**
 * Animated AI Coach persona — web port of Flutter AnimatedAIPersona.
 */
(function () {
  const AI1 = '#FF9466';
  const AI2 = '#C755FF';
  const BLUE = '#1e60ff';
  const LOGICAL = 320;

  function lerp(a, b, t) {
    return a + (b - a) * t;
  }

  function easeInOut(t) {
    return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
  }

  function interpolateDouble(phase, keyTimes, values) {
    if (!keyTimes.length) return 0;
    if (phase <= keyTimes[0]) return values[0];
    if (phase >= keyTimes[keyTimes.length - 1]) return values[values.length - 1];
    for (let i = 0; i < keyTimes.length - 1; i++) {
      if (phase >= keyTimes[i] && phase <= keyTimes[i + 1]) {
        const t = (phase - keyTimes[i]) / (keyTimes[i + 1] - keyTimes[i]);
        return lerp(values[i], values[i + 1], easeInOut(t));
      }
    }
    return values[0];
  }

  function hexToRgb(hex) {
    const h = hex.replace('#', '');
    const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
  }

  function rgba(hex, a) {
    const { r, g, b } = hexToRgb(hex);
    return 'rgba(' + r + ',' + g + ',' + b + ',' + a + ')';
  }

  function lerpColor(a, b, t) {
    const A = hexToRgb(a);
    const B = hexToRgb(b);
    const r = Math.round(lerp(A.r, B.r, t));
    const g = Math.round(lerp(A.g, B.g, t));
    const bl = Math.round(lerp(A.b, B.b, t));
    return '#' + [r, g, bl].map((v) => v.toString(16).padStart(2, '0')).join('');
  }

  function roundRectPath(ctx, x, y, w, h, r) {
    const rr = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + rr, y);
    ctx.arcTo(x + w, y, x + w, y + h, rr);
    ctx.arcTo(x + w, y + h, x, y + h, rr);
    ctx.arcTo(x, y + h, x, y, rr);
    ctx.arcTo(x, y, x + w, y, rr);
    ctx.closePath();
  }

  function drawDriftingBlob(ctx, opts) {
    const cx = opts.baseCenter.x + opts.driftDistX * Math.sin(opts.t * opts.speedX + opts.phaseX);
    const cy = opts.baseCenter.y + opts.driftDistY * Math.cos(opts.t * opts.speedY + opts.phaseY);
    const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, opts.radius);
    grad.addColorStop(0, rgba(opts.color, opts.baseOpacity));
    grad.addColorStop(1, rgba(opts.color, 0));
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(cx, cy, opts.radius, 0, Math.PI * 2);
    ctx.fill();
  }

  function drawFace(ctx, t) {
    const size = LOGICAL;

    ctx.save();
    const inset = 10;
    roundRectPath(ctx, inset, inset, size - inset * 2, size - inset * 2, 110);
    ctx.clip();

    const bg = ctx.createLinearGradient(0, 0, size, size);
    bg.addColorStop(0, AI1);
    bg.addColorStop(1, lerpColor(AI1, AI2, 0.45));
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, size, size);

    const yellowRadius = 200 + 30 * Math.sin(t * ((2 * Math.PI) / 12));
    const yellowOpacity = Math.min(1, Math.max(0, 0.85 + 0.1 * Math.cos(t * ((2 * Math.PI) / 18))));
    drawDriftingBlob(ctx, {
      color: lerpColor(AI1, '#FFF2AF', 0.4),
      baseCenter: { x: 95, y: 95 },
      radius: yellowRadius,
      t: t,
      speedX: (2 * Math.PI) / 10,
      speedY: (2 * Math.PI) / 12,
      driftDistX: 45,
      driftDistY: 35,
      phaseX: 0,
      phaseY: Math.PI / 4,
      baseOpacity: yellowOpacity,
    });

    const purpleRadius = 230 + 35 * Math.cos(t * ((2 * Math.PI) / 12));
    const purpleOpacity = Math.min(1, Math.max(0, 0.75 + 0.15 * Math.sin(t * ((2 * Math.PI) / 10))));
    drawDriftingBlob(ctx, {
      color: AI2,
      baseCenter: { x: 170, y: 190 },
      radius: purpleRadius,
      t: t,
      speedX: (2 * Math.PI) / 15,
      speedY: (2 * Math.PI) / 8,
      driftDistX: 55,
      driftDistY: 50,
      phaseX: Math.PI / 2,
      phaseY: 0,
      baseOpacity: purpleOpacity,
    });

    const blueRadius = 210 + 35 * Math.sin(t * ((2 * Math.PI) / 18));
    const blueOpacity = Math.min(1, Math.max(0, 0.7 + 0.15 * Math.cos(t * ((2 * Math.PI) / 12))));
    drawDriftingBlob(ctx, {
      color: BLUE,
      baseCenter: { x: 230, y: 230 },
      radius: blueRadius,
      t: t,
      speedX: (2 * Math.PI) / 9,
      speedY: (2 * Math.PI) / 10,
      driftDistX: 55,
      driftDistY: 45,
      phaseX: Math.PI,
      phaseY: Math.PI / 3,
      baseOpacity: blueOpacity,
    });

    ctx.restore();

    ctx.save();
    ctx.globalCompositeOperation = 'destination-in';
    const mask = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size * 0.475);
    mask.addColorStop(0, 'rgba(0,0,0,1)');
    mask.addColorStop(0.74, 'rgba(0,0,0,1)');
    mask.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = mask;
    ctx.fillRect(0, 0, size, size);
    ctx.restore();
  }

  function drawEye(ctx, x, y, scaleY, translateY, glowOpacity) {
    ctx.save();
    ctx.globalAlpha = Math.max(0, Math.min(1, glowOpacity));
    const glow = ctx.createRadialGradient(x + 21, y + 31, 0, x + 21, y + 31, 36);
    glow.addColorStop(0, 'rgba(255,255,255,0.6)');
    glow.addColorStop(0.7, 'rgba(255,255,255,0)');
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.ellipse(x + 21, y + 31, 29, 39, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;

    ctx.translate(x + 21, y + 31 + translateY);
    ctx.scale(1, Math.max(0.04, scaleY));
    roundRectPath(ctx, -21, -31, 42, 62, 21);
    ctx.fillStyle = '#ffffff';
    ctx.fill();
    ctx.restore();
  }

  function mount(root) {
    const canvas = root.querySelector('canvas');
    if (!canvas) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const display = root.getBoundingClientRect().width || parseFloat(root.dataset.size) || 96;
    canvas.width = LOGICAL * dpr;
    canvas.height = LOGICAL * dpr;
    canvas.style.width = display + 'px';
    canvas.style.height = display + 'px';

    const ctx = canvas.getContext('2d');
    const faceCanvas = document.createElement('canvas');
    faceCanvas.width = LOGICAL * dpr;
    faceCanvas.height = LOGICAL * dpr;
    const fctx = faceCanvas.getContext('2d');

    let start = null;
    let raf = 0;
    let visible = true;

    const faceKeys = [0, 0.2, 0.45, 0.65, 0.78, 0.82, 0.88, 1];
    const faceScaleXVals = [1, 1, 1.04, 1.04, 1.06, 0.97, 1, 1];
    const faceScaleYVals = [1, 1, 1.04, 1.04, 0.97, 1.05, 1, 1];
    const faceTYVals = [0, 0, -4, -4, 3, -5, 0, 0];

    const eyeTXKeys = [0, 0.15, 0.25, 0.4, 0.5, 0.65, 0.75, 1];
    const eyeTXVals = [0, 0, -13, -13, 13, 13, 0, 0];
    const eyeBlinkKeys = [0, 0.09, 0.12, 0.15, 0.17, 0.19, 0.45, 0.65, 0.78, 0.88, 0.91, 1];
    const eyeTYVals = [0, 0, 0, 0, 0, 0, 0, 0, 6, 6, 0, 0];
    const eyeSYVals = [1, 1, 0.04, 1, 0.04, 1, 1.18, 1.18, 0.5, 0.5, 1, 1];
    const eyeGlowVals = [1, 1, 0, 1, 0, 1, 1, 1, 0.7, 0.7, 1, 1];

    function renderStatic() {
      fctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      fctx.clearRect(0, 0, LOGICAL, LOGICAL);
      drawFace(fctx, 0);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, LOGICAL, LOGICAL);
      ctx.drawImage(faceCanvas, 0, 0, LOGICAL, LOGICAL);
      drawEye(ctx, 97, 101, 1, 0, 1);
      drawEye(ctx, 181, 101, 1, 0, 1);
    }

    function frame(ts) {
      if (!visible) {
        raf = 0;
        return;
      }
      if (start == null) start = ts;
      const t = ((ts - start) / 1000) % 360;
      const emotionPhase = (t % 10) / 10;

      const faceScaleX = interpolateDouble(emotionPhase, faceKeys, faceScaleXVals);
      const faceScaleY = interpolateDouble(emotionPhase, faceKeys, faceScaleYVals);
      const faceTranslateY = interpolateDouble(emotionPhase, faceKeys, faceTYVals);

      const eyeTranslateX = interpolateDouble(emotionPhase, eyeTXKeys, eyeTXVals);
      const eyeTranslateY = interpolateDouble(emotionPhase, eyeBlinkKeys, eyeTYVals);
      const eyeScaleY = interpolateDouble(emotionPhase, eyeBlinkKeys, eyeSYVals);
      const eyeGlowOpacity = interpolateDouble(emotionPhase, eyeBlinkKeys, eyeGlowVals);

      fctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      fctx.clearRect(0, 0, LOGICAL, LOGICAL);
      drawFace(fctx, t);

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, LOGICAL, LOGICAL);
      ctx.save();
      ctx.translate(LOGICAL / 2, LOGICAL / 2 + faceTranslateY);
      ctx.scale(faceScaleX, faceScaleY);
      ctx.translate(-LOGICAL / 2, -LOGICAL / 2);
      ctx.drawImage(faceCanvas, 0, 0, LOGICAL, LOGICAL);
      drawEye(ctx, 97 + eyeTranslateX, 101, eyeScaleY, eyeTranslateY, eyeGlowOpacity);
      drawEye(ctx, 181 + eyeTranslateX, 101, eyeScaleY, eyeTranslateY, eyeGlowOpacity);
      ctx.restore();

      raf = requestAnimationFrame(frame);
    }

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      renderStatic();
      return;
    }

    const io = new IntersectionObserver(
      function (entries) {
        visible = entries.some(function (e) {
          return e.isIntersecting;
        });
        if (visible && !raf) raf = requestAnimationFrame(frame);
      },
      { threshold: 0.05 }
    );
    io.observe(root);
    raf = requestAnimationFrame(frame);
  }

  function init() {
    document.querySelectorAll('[data-gpath-coach-persona]').forEach(mount);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
